<template>
  <v-main>
    <v-container fluid>
      <div class="breadcrumbs-container">
        <Breadcrumbs />
      </div>
      <div class="ar-root">
        <!-- ── Page Header ── -->
        <div class="ar-page-header">
          <div class="d-flex align-center">
            <div class="ar-header-icon mr-3">
              <v-icon color="white" size="26">mdi-clipboard-check-multiple-outline</v-icon>
            </div>
            <div>
              <div class="white--text text-h6 font-weight-bold">
                {{ $tc('assessmentResults.title') }}
              </div>
              <div class="white--text text-caption" style="opacity:0.75;">
                {{ sacInfo.name }} &nbsp;·&nbsp; {{ socInfo.name }}
                <span v-if="standardInfo">&nbsp;·&nbsp; {{ standardInfo }}</span>
              </div>
            </div>
          </div>
          <!-- Overall Score Pill -->
          <div class="ar-score-pill">
            <div class="ar-score-label">Overall Score</div>
            <div class="ar-score-value" :style="{ color: scoreHex(totalScore) }">
              {{ totalScore }}%
            </div>
            <v-progress-linear
              :value="totalScore"
              :color="getScoreColor(totalScore)"
              height="5"
              rounded
              class="mt-1"
            />
          </div>
        </div>

        <!-- ── Stats Row ── -->
        <v-row class="ar-stats-row" no-gutters>
          <v-col v-for="stat in statsCards" :key="stat.label" cols="6" sm="4" md="2" class="pa-2">
            <div class="ar-stat-card" :style="{ borderLeft: '3px solid ' + stat.color }">
              <div class="ar-stat-icon" :style="{ background: stat.bg }">
                <v-icon :color="stat.color" size="20">{{ stat.icon }}</v-icon>
              </div>
              <div class="ar-stat-body">
                <div class="ar-stat-number" :style="{ color: stat.color }">{{ stat.value }}</div>
                <div class="ar-stat-label">{{ stat.label }}</div>
                <div v-if="stat.sub" class="ar-stat-sub">{{ stat.sub }}</div>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- ── Score Gauges Row ── -->
        <v-row class="px-2 mb-2" no-gutters>
          <v-col cols="12" sm="4" class="pa-2" v-for="gauge in gauges" :key="gauge.label">
            <div class="ar-gauge-card">
              <div class="ar-gauge-header">
                <v-icon size="16" :color="gauge.color" class="mr-1">{{ gauge.icon }}</v-icon>
                <span class="text-caption font-weight-bold" style="color:#546e7a;">{{
                  gauge.label
                }}</span>
                <v-spacer />
                <span class="ar-gauge-score" :style="{ color: scoreHex(gauge.score) }">
                  {{ gauge.score }}%
                </span>
              </div>
              <v-progress-linear
                :value="gauge.score"
                :color="getScoreColor(gauge.score)"
                height="8"
                rounded
                class="mt-2"
              />
              <div class="ar-gauge-meta mt-1">
                <span class="text-caption" style="color:#90a4ae;">{{ gauge.meta }}</span>
              </div>
            </div>
          </v-col>
        </v-row>

        <!-- ── Evidence Status Summary ── -->
        <div class="ar-section-card px-4 pb-3 mb-3">
          <div class="ar-section-header">
            <v-icon color="white" size="16" class="mr-2">mdi-file-check-outline</v-icon>
            <span class="white--text text-caption font-weight-bold"
              >Evidence Status &amp; Compliance Breakdown</span
            >
            <v-spacer />
            <v-chip x-small color="rgba(255,255,255,0.2)" text-color="white">
              <v-icon x-small left>mdi-shield-check-outline</v-icon>
              Compliance: {{ evidenceComplianceScore }}%
            </v-chip>
          </div>
          <!-- Compliance Score Progress Bar -->
          <div class="ar-compliance-bar px-2 pt-3 pb-1">
            <div class="d-flex align-center justify-space-between mb-1">
              <span class="text-caption font-weight-bold" style="color:#546e7a;">
                <v-icon
                  x-small
                  class="mr-1"
                  :color="
                    evidenceComplianceScore >= 80
                      ? '#2e7d32'
                      : evidenceComplianceScore >= 60
                      ? '#e65100'
                      : '#c62828'
                  "
                  >mdi-shield-check-outline</v-icon
                >
                Evidence Compliance Score
              </span>
              <span
                class="text-caption font-weight-bold"
                :style="{
                  color:
                    evidenceComplianceScore >= 80
                      ? '#2e7d32'
                      : evidenceComplianceScore >= 60
                      ? '#e65100'
                      : '#c62828',
                }"
              >
                {{ evidenceComplianceScore }}%
              </span>
            </div>
            <v-progress-linear
              :value="evidenceComplianceScore"
              :color="
                evidenceComplianceScore >= 80
                  ? 'success'
                  : evidenceComplianceScore >= 60
                  ? 'warning'
                  : 'error'
              "
              height="10"
              rounded
              background-color="rgba(0,0,0,0.06)"
            />
            <div class="d-flex justify-space-between mt-1">
              <span class="text-caption" style="color:#90a4ae;">0 — Non-Compliant</span>
              <span class="text-caption" style="color:#90a4ae;"
                >{{ evidences.filter(e => e.complianceScore != null).length }} of
                {{ evidences.length }} scored</span
              >
              <span class="text-caption" style="color:#90a4ae;">100 — Fully Compliant</span>
            </div>
          </div>
          <v-row class="pt-2" no-gutters>
            <v-col
              v-for="ev in evidenceStatusBreakdown"
              :key="ev.label"
              cols="6"
              sm="4"
              class="pa-2"
            >
              <div class="ar-ev-chip" :style="{ borderColor: ev.color }">
                <v-icon :color="ev.color" size="18" class="mr-1">{{ ev.icon }}</v-icon>
                <div>
                  <div class="text-subtitle-1 font-weight-bold" :style="{ color: ev.color }">
                    {{ ev.count }}
                  </div>
                  <div class="text-caption" style="color:#78909c;">{{ ev.label }}</div>
                </div>
              </div>
            </v-col>
          </v-row>
        </div>

        <!-- ── Requirements Status Bar Chart ── -->
        <div class="ar-section-card px-4 pb-4 mb-3">
          <div class="ar-section-header">
            <v-icon color="white" size="16" class="mr-2">mdi-format-list-checks</v-icon>
            <span class="white--text text-caption font-weight-bold"
              >Requirements Score Distribution</span
            >
          </div>
          <div class="pt-3">
            <HorizontalBarChart
              v-if="mappedRequirementsScores.length"
              :scores="mappedRequirementsScores"
              chart-label="Requirements"
            />
            <div v-else class="text-center text-caption py-6" style="color:#b0bec5;">
              No requirements data available
            </div>
          </div>
        </div>

        <!-- ── Claims & Sub-Claims Tabs ── -->
        <div class="ar-section-card mb-3">
          <div class="ar-section-header">
            <v-icon color="white" size="16" class="mr-2">mdi-layers-outline</v-icon>
            <span class="white--text text-caption font-weight-bold">Claims &amp; Sub-Claims</span>
            <v-spacer />
            <v-chip
              v-if="activeTab === 0"
              x-small
              color="rgba(255,255,255,0.2)"
              text-color="white"
              class="ml-2"
            >
              Avg {{ averageClaimsScore }}%
            </v-chip>
            <v-chip v-else x-small color="rgba(255,255,255,0.2)" text-color="white" class="ml-2">
              Avg {{ averageSubClaimsScore }}%
            </v-chip>
          </div>
          <v-tabs v-model="activeTab" color="primary" slider-color="primary" class="px-2 pt-2">
            <v-tab class="text-capitalize text-subtitle-2">
              <v-icon left small>mdi-check-decagram-outline</v-icon>
              Claims
              <v-chip
                x-small
                class="ml-2"
                :color="
                  averageClaimsScore >= 80
                    ? 'success'
                    : averageClaimsScore >= 60
                    ? 'warning'
                    : 'error'
                "
                dark
              >
                {{ claimsScores.length }}
              </v-chip>
            </v-tab>
            <v-tab class="text-capitalize text-subtitle-2">
              <v-icon left small>mdi-subdirectory-arrow-right</v-icon>
              Sub-Claims
              <v-chip
                x-small
                class="ml-2"
                :color="
                  averageSubClaimsScore >= 80
                    ? 'success'
                    : averageSubClaimsScore >= 60
                    ? 'warning'
                    : 'error'
                "
                dark
              >
                {{ subClaimsScores.length }}
              </v-chip>
            </v-tab>
          </v-tabs>
          <v-tabs-items v-model="activeTab">
            <!-- Claims -->
            <v-tab-item>
              <v-card flat>
                <v-card-text class="pt-2">
                  <v-row>
                    <v-col
                      v-for="claim in paginatedClaims"
                      :key="claim.id"
                      cols="12"
                      md="6"
                      class="pa-2"
                    >
                      <div class="ar-item-card">
                        <div class="ar-item-header">
                          <div class="ar-item-title">{{ claim.title }}</div>
                          <div
                            class="ar-score-badge"
                            :style="{
                              background: scoreHex(claim.score) + '18',
                              color: scoreHex(claim.score),
                            }"
                          >
                            {{ claim.score }}%
                          </div>
                        </div>
                        <v-progress-linear
                          :value="claim.score"
                          :color="getScoreColor(claim.score)"
                          height="5"
                          rounded
                          class="my-2"
                        />
                        <div class="d-flex align-center" style="gap:6px;">
                          <v-chip x-small color="primary" outlined>
                            <v-icon left x-small>mdi-subdirectory-arrow-right</v-icon>
                            {{ getClaimSubClaimsCount(claim.id) }} sub-claims
                          </v-chip>
                          <v-chip x-small :color="getScoreColor(claim.score)" dark>
                            {{
                              claim.score >= 80 ? 'Strong' : claim.score >= 60 ? 'Moderate' : 'Weak'
                            }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-pagination
                    v-if="claimsScores.length > itemsPerPage"
                    v-model="claimsPage"
                    :length="Math.ceil(claimsScores.length / itemsPerPage)"
                    :total-visible="7"
                    class="mt-3"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>

            <!-- Sub-Claims -->
            <v-tab-item>
              <v-card flat>
                <v-card-text class="pt-2">
                  <v-row>
                    <v-col
                      v-for="subClaim in paginatedSubClaims"
                      :key="subClaim.id"
                      cols="12"
                      md="6"
                      class="pa-2"
                    >
                      <div class="ar-item-card">
                        <div class="ar-item-header">
                          <div class="ar-item-title">{{ subClaim.title }}</div>
                          <div
                            class="ar-score-badge"
                            :style="{
                              background: scoreHex(subClaim.score) + '18',
                              color: scoreHex(subClaim.score),
                            }"
                          >
                            {{ subClaim.score }}%
                          </div>
                        </div>
                        <v-progress-linear
                          :value="subClaim.score"
                          :color="getScoreColor(subClaim.score)"
                          height="5"
                          rounded
                          class="my-2"
                        />
                        <div class="d-flex align-center" style="gap:6px;">
                          <v-chip x-small color="info" outlined>
                            <v-icon left x-small>mdi-paperclip</v-icon>
                            {{ getSubClaimEvidencesCount(subClaim.id) }} evidences
                          </v-chip>
                          <v-chip x-small :color="getScoreColor(subClaim.score)" dark>
                            {{
                              subClaim.score >= 80
                                ? 'Strong'
                                : subClaim.score >= 60
                                ? 'Moderate'
                                : 'Weak'
                            }}
                          </v-chip>
                        </div>
                      </div>
                    </v-col>
                  </v-row>
                  <v-pagination
                    v-if="subClaimsScores.length > itemsPerPage"
                    v-model="subClaimsPage"
                    :length="Math.ceil(subClaimsScores.length / itemsPerPage)"
                    :total-visible="7"
                    class="mt-3"
                  />
                </v-card-text>
              </v-card>
            </v-tab-item>
          </v-tabs-items>
        </div>
      </div>
    </v-container>
  </v-main>
</template>

<script>
import subClaimsApi from '@/modules/sac/api/subClaimsApi';
import claimsApi from '@/modules/sac/api/claimsApi';
import inScopeRequirementsApi from '@/modules/sac/api/inScopeRequirementsApi';
import EventBus from '@/helpers/event-bus.js';
// import EvidenceStatusBarChart from '@/components/charts/EvidenceStatusBarChart';
// import MultiRadialProgress from '@/components/charts/RadialChart';
import HorizontalBarChart from '@/components/charts/ScoreBarChart';
import Breadcrumbs from '@/components/charts/Breadcrumbs.vue';

export default {
  name: 'AssessmentResults',
  components: {
    // EvidenceStatusBarChart,
    // MultiRadialProgress,
    HorizontalBarChart,
    Breadcrumbs,
  },
  data() {
    return {
      subClaimsScores: [],
      claimsScores: [],
      mappedRequirementsScores: [],
      evidences: [],
      scores: [],
      search: null,
      activeTab: 0,
      claimsPage: 1,
      subClaimsPage: 1,
      itemsPerPage: 4,
      standardInfo: null,
      sacInfo: { name: '', description: '', id: null },
      socInfo: { name: '', type: '', standardFamily: null },
      allClaims: [],
      allSubClaims: [],
    };
  },
  created() {
    this.init();
  },
  computed: {
    paginatedClaims() {
      const start = (this.claimsPage - 1) * this.itemsPerPage;
      return this.claimsScores.slice(start, start + this.itemsPerPage);
    },
    paginatedSubClaims() {
      const start = (this.subClaimsPage - 1) * this.itemsPerPage;
      return this.subClaimsScores.slice(start, start + this.itemsPerPage);
    },
    averageClaimsScore() {
      if (!this.claimsScores.length) return 0;
      return Math.round(
        this.claimsScores.reduce((s, c) => s + Number(c.score), 0) / this.claimsScores.length,
      );
    },
    averageSubClaimsScore() {
      if (!this.subClaimsScores.length) return 0;
      return Math.round(
        this.subClaimsScores.reduce((s, c) => s + Number(c.score), 0) / this.subClaimsScores.length,
      );
    },
    totalScore() {
      return this.$route.query.score;
    },
    totalSubClaimsInClaims() {
      return this.allClaims.reduce((t, c) => t + (c.subClaims ? c.subClaims.length : 0), 0);
    },
    totalEvidencesInSubClaims() {
      return this.allSubClaims.reduce((t, s) => t + (s.evidences ? s.evidences.length : 0), 0);
    },
    statsCards() {
      return [
        {
          label: 'Requirements',
          value: this.mappedRequirementsScores.length,
          sub: null,
          icon: 'mdi-format-list-checks',
          color: '#1565c0',
          bg: 'rgba(21,101,192,0.08)',
        },
        {
          label: 'Claims',
          value: this.claimsScores.length,
          sub: `${this.totalSubClaimsInClaims} sub-claims`,
          icon: 'mdi-check-decagram-outline',
          color: '#6a1b9a',
          bg: 'rgba(106,27,154,0.08)',
        },
        {
          label: 'Sub-Claims',
          value: this.subClaimsScores.length,
          sub: `${this.totalEvidencesInSubClaims} evidences`,
          icon: 'mdi-subdirectory-arrow-right',
          color: '#00695c',
          bg: 'rgba(0,105,92,0.08)',
        },
        {
          label: 'Evidences',
          value: this.evidences.length,
          sub: `${this.evidenceStatusBreakdown.find(e => e.label === 'Approved')?.count ||
            0} approved`,
          icon: 'mdi-paperclip',
          color: '#e65100',
          bg: 'rgba(230,81,0,0.08)',
        },
        {
          label: 'Compliance Score',
          value: `${this.evidenceComplianceScore}%`,
          sub: `${this.evidences.filter(e => e.complianceScore != null).length} scored`,
          icon: 'mdi-shield-check-outline',
          color:
            this.evidenceComplianceScore >= 80
              ? '#2e7d32'
              : this.evidenceComplianceScore >= 60
              ? '#e65100'
              : '#c62828',
          bg:
            this.evidenceComplianceScore >= 80
              ? 'rgba(46,125,50,0.08)'
              : this.evidenceComplianceScore >= 60
              ? 'rgba(230,81,0,0.08)'
              : 'rgba(198,40,40,0.08)',
        },
      ];
    },
    gauges() {
      return [
        {
          label: 'Claims Average Score',
          score: this.averageClaimsScore,
          icon: 'mdi-check-decagram-outline',
          color: '#6a1b9a',
          meta: `${this.claimsScores.length} claims evaluated`,
        },
        {
          label: 'Sub-Claims Average Score',
          score: this.averageSubClaimsScore,
          icon: 'mdi-subdirectory-arrow-right',
          color: '#00695c',
          meta: `${this.subClaimsScores.length} sub-claims evaluated`,
        },
        {
          label: 'Evidence Compliance Score',
          score: this.evidenceComplianceScore,
          icon: 'mdi-shield-check-outline',
          color:
            this.evidenceComplianceScore >= 80
              ? '#2e7d32'
              : this.evidenceComplianceScore >= 60
              ? '#e65100'
              : '#c62828',
          meta: `${this.evidences.filter(e => e.complianceScore != null).length} of ${
            this.evidences.length
          } scored`,
        },
      ];
    },
    evidenceStatusBreakdown() {
      const statuses = [
        { label: 'Approved', color: '#2e7d32', icon: 'mdi-check-circle-outline' },
        { label: 'Largely Compliant', color: '#f57f17', icon: 'mdi-check-decagram-outline' },
        { label: 'Pending Review', color: '#1565c0', icon: 'mdi-clock-outline' },
        { label: 'Reviewed', color: '#e65100', icon: 'mdi-alert-circle-outline' },
        { label: 'Rejected', color: '#c62828', icon: 'mdi-close-circle-outline' },
      ];
      return statuses.map(s => ({
        ...s,
        count: this.evidences.filter(e => e.status === s.label).length,
      }));
    },
    evidenceCoverageScore() {
      if (!this.evidences.length) return 0;
      return Math.round(
        (this.evidences.filter(e => e.status === 'Approved').length / this.evidences.length) * 100,
      );
    },
    evidenceComplianceScore() {
      const scored = this.evidences.filter(e => e.complianceScore != null);
      if (!scored.length) return 0;
      const sumNormalized = scored.reduce((sum, e) => sum + e.complianceScore / 5, 0);
      return Math.round((sumNormalized / scored.length) * 100);
    },
  },
  methods: {
    async init() {
      await this.getScores();
      await this.findScoresForSac();
    },
    async getScores() {
      try {
        const [
          { data: subClaimScores },
          { data: claimScores },
          { data: mappedReqScores },
        ] = await Promise.all([
          subClaimsApi.getScores(),
          claimsApi.getScores(),
          inScopeRequirementsApi.getScores(),
        ]);
        this.subClaimsScores = subClaimScores;
        this.claimsScores = claimScores;
        this.mappedRequirementsScores = mappedReqScores;
      } catch (error) {
        if (error.response?.status === 400) {
          EventBus.$emit('snackbar', { text: this.$t('errorList.error-404'), color: 'grey' });
        }
      }
      this.scores = this.claimsScores;
    },
    async findScoresForSac() {
      try {
        const asset = this.$route.query;
        const { data: inScopeRequirements } = await inScopeRequirementsApi.all(asset.socId);

        this.sacInfo = {
          name:
            asset.sac ||
            localStorage.getItem('currentSacName') ||
            'Current Security Assurance Case',
          id: asset.sacId,
        };
        this.socInfo = {
          name: asset.soc || localStorage.getItem('currentSocName') || 'Current SoC',
          standardFamily: asset.standardFamily,
        };

        let filteredRequirements = inScopeRequirements;
        if (asset.standardFamily) {
          filteredRequirements = inScopeRequirements.filter(r => r.family === asset.standardFamily);
          if (!filteredRequirements.length) filteredRequirements = inScopeRequirements;
        }

        this.standardInfo =
          asset.standardFamily ||
          (filteredRequirements.length > 0 ? filteredRequirements[0].family : 'Unknown');

        const allClaimsResponses = await Promise.all(
          filteredRequirements.map(r => claimsApi.getMappedClaims(r.id)),
        );
        this.allClaims = allClaimsResponses.flatMap(r => r.data || []);

        this.mappedRequirementsScores = this.mappedRequirementsScores.filter(s =>
          filteredRequirements.some(r => r.id === s.id),
        );
        this.claimsScores = this.claimsScores.filter(s => this.allClaims.some(c => c.id === s.id));
        this.allSubClaims = this.allClaims.flatMap(c => c.subClaims || []);
        this.subClaimsScores = this.subClaimsScores.filter(s =>
          this.allSubClaims.some(sc => sc.id === s.id),
        );
        this.evidences = this.allSubClaims.flatMap(sc => sc.evidences || []);
      } catch (error) {
        console.error('Error in findScoresForSac:', error);
        EventBus.$emit('snackbar', { text: this.$t('errorList.error-404'), color: 'grey' });
      }
    },
    getClaimSubClaimsCount(claimId) {
      const claim = this.allClaims.find(c => c.id == claimId);
      return claim?.subClaims?.length || 0;
    },
    getSubClaimEvidencesCount(subClaimId) {
      const sc = this.allSubClaims.find(s => s.id == subClaimId);
      return sc?.evidences?.length || 0;
    },
    statusChipColor(status) {
      return status === 'Draft' ? 'orange' : status === 'Completed' ? 'green' : 'primary';
    },
    getScoreColor(score) {
      if (score >= 80) return 'success';
      if (score >= 60) return 'warning';
      return 'error';
    },
    scoreHex(score) {
      if (score >= 80) return '#2e7d32';
      if (score >= 60) return '#e65100';
      return '#c62828';
    },
  },
};
</script>

<style scoped>
/* ── Root ── */
.ar-root {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── Breadcrumbs ── */
.breadcrumbs-container {
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 20px;
  padding-bottom: 24px;
}
.breadcrumbs-container .v-breadcrumbs {
  background: #ffffff;
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 800;
}

/* ── Page Header ── */
.ar-page-header {
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%);
  border-radius: 14px;
  padding: 20px 28px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  box-shadow: 0 4px 18px rgba(26, 35, 126, 0.18);
}
.ar-header-icon {
  width: 48px;
  height: 48px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ar-score-pill {
  background: rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  padding: 12px 20px;
  min-width: 140px;
  backdrop-filter: blur(4px);
}
.ar-score-label {
  color: rgba(255, 255, 255, 0.7);
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ar-score-value {
  font-size: 1.8rem;
  font-weight: 800;
  line-height: 1.1;
}

/* ── Stats Row ── */
.ar-stats-row {
  margin: 0 -8px;
}
.ar-stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  height: 100%;
  transition: box-shadow 0.2s, transform 0.2s;
}
.ar-stat-card:hover {
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}
.ar-stat-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.ar-stat-number {
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
  color: #263238;
}
.ar-stat-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: #78909c;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-top: 2px;
}
.ar-stat-sub {
  font-size: 0.7rem;
  color: #90a4ae;
  margin-top: 2px;
}

/* ── Gauge Cards ── */
.ar-gauge-card {
  background: #fff;
  border-radius: 12px;
  padding: 14px 16px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  height: 100%;
}
.ar-gauge-header {
  display: flex;
  align-items: center;
}
.ar-gauge-score {
  font-size: 1rem;
  font-weight: 800;
}
.ar-gauge-meta {
  text-align: right;
}

/* ── Section Cards ── */
.ar-section-card {
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
}
.ar-section-header {
  background: linear-gradient(135deg, #37474f 0%, #455a64 100%);
  padding: 10px 16px;
  display: flex;
  align-items: center;
}

/* ── Evidence Chips ── */
.ar-ev-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  background: #fafbfd;
  border: 2px solid;
  border-radius: 10px;
  padding: 10px 14px;
  height: 100%;
}

/* ── Item Cards (claims/subclaims) ── */
.ar-item-card {
  background: #fafbfd;
  border: 1px solid #e8eaf6;
  border-radius: 12px;
  padding: 14px 16px;
  height: 100%;
  transition: box-shadow 0.2s, transform 0.2s;
}
.ar-item-card:hover {
  box-shadow: 0 4px 14px rgba(57, 73, 171, 0.1);
  transform: translateY(-2px);
}
.ar-item-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}
.ar-item-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #263238;
  flex: 1;
  min-width: 0;
}
.ar-score-badge {
  font-size: 0.78rem;
  font-weight: 800;
  padding: 2px 8px;
  border-radius: 20px;
  white-space: nowrap;
  flex-shrink: 0;
}

/* ── Compliance Bar ── */
.ar-compliance-bar {
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 4px;
}

/* ── Stat card accent border ── */
.ar-stat-card {
  border-left-width: 3px !important;
  border-left-style: solid;
}
</style>
